/**
 * The Library: the indexed corpus, plus upload.
 *
 * Reads `GET /corpus/documents`, NOT `GET /documents`. The latter lists this API
 * process's in-memory upload-job store: it dies on restart, is invisible to other
 * replicas, and never sees a Kafka-ingested document. It answers "what did I just
 * upload here", which is a different question from "what is in the library".
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { FacetSelect } from "@/components/FacetSelect.tsx";
import { TopBar } from "@/components/TopBar.tsx";
import { UploadPanel } from "@/components/UploadPanel.tsx";
import { Button, EmptyState, Input, Pill } from "@/components/primitives.tsx";
import { formatCount } from "@/i18n/index.ts";
import { api } from "@/lib/api.ts";
import type { CorpusDocument, DocumentStatus } from "@/lib/api-types.ts";
import { parseLegalTitle, truncateAtWord } from "@/lib/legalTitle.ts";

const PAGE = 50;

/** `needs_review` IS a corpus status since migration 0020 — a fully-quarantined
 *  document persists it instead of sitting at `processing` forever. */
const STATUSES: DocumentStatus[] = [
  "indexed",
  "needs_review",
  "processing",
  "pending",
  "failed",
  "archived",
];



export function LibraryRoute() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<DocumentStatus | "">("");
  const [year, setYear] = useState("");
  const [wilayah, setWilayah] = useState("");
  const [limit, setLimit] = useState(PAGE);

  const collections = useQuery({ queryKey: ["collections"], queryFn: api.collections });
  // The dropdown vocabularies. Server-side because the page below is 50 rows of several
  // thousand, so deriving them here would offer only what happens to be near the top.
  // Keyed on the OTHER filters too, so the counts update as the corpus narrows. Each
  // facet drops its own filter server-side, which is what keeps the list from collapsing
  // to the single value already chosen.
  const facets = useQuery({
    queryKey: ["corpus", "facets", search, status, year, wilayah],
    queryFn: () =>
      api.corpusFacets({
        search: search || undefined,
        status: status || undefined,
        year: year || undefined,
        wilayah: wilayah || undefined,
      }),
  });
  const documents = useQuery({
    queryKey: ["corpus", "library", search, status, year, wilayah, limit],
    queryFn: () =>
      api.corpus({
        limit,
        search: search || undefined,
        status: status || undefined,
        year: year || undefined,
        wilayah: wilayah || undefined,
      }),
  });

  return (
    <div className="lib">
      <TopBar />
      <div className="wrap">
        <div className="row" style={{ marginBottom: "var(--s4)" }}>
          <div>
            <h1 className="page-title">{t("library.title")}</h1>
            <p className="page-sub">{t("library.subtitle")}</p>
          </div>
        </div>

        <UploadPanel
          collections={collections.data ?? []}
          onUploaded={() => {
            void queryClient.invalidateQueries({ queryKey: ["corpus"] });
            void queryClient.invalidateQueries({ queryKey: ["collections"] });
          }}
        />

        <div className="row" style={{ margin: "var(--s5) 0 var(--s3)" }}>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("library.search")}
            aria-label={t("library.search")}
            style={{ flex: 1, maxWidth: 320 }}
          />
          <select
            className="input"
            aria-label={t("library.columns.status")}
            value={status}
            onChange={(event) => setStatus(event.target.value as DocumentStatus | "")}
            style={{ width: 170 }}
          >
            <option value="">{t("common.all")}</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`library.status.${value}`)}
              </option>
            ))}
          </select>
          <FacetSelect
            label={t("library.columns.region")}
            allLabel={t("library.allRegions")}
            value={wilayah}
            options={facets.data?.regions ?? []}
            onChange={setWilayah}
            loading={facets.isLoading}
          />
          <FacetSelect
            label={t("library.columns.year")}
            allLabel={t("library.allYears")}
            value={year}
            options={facets.data?.years ?? []}
            onChange={setYear}
            loading={facets.isLoading}
          />
          <span className="spacer" />
          {documents.data ? (
            <span className="faint mono">
              {t("library.total", {
                shown: formatCount(documents.data.documents.length, i18n.language),
                total: formatCount(documents.data.total, i18n.language),
              })}
            </span>
          ) : null}
        </div>

        {documents.data && documents.data.documents.length === 0 ? (
          <EmptyState title={t("library.emptyTitle")} body={t("library.emptyBody")} />
        ) : (
          <div className="grid-wrap">
            <table style={{ width: "100%", borderCollapse: "collapse", background: "var(--surface)" }}>
              <thead>
                <tr>
                  <th style={cell}>{t("library.columns.title")}</th>
                  <th style={cell}>{t("library.columns.year")}</th>
                  <th style={cell}>{t("library.columns.region")}</th>
                  <th style={cell}>{t("library.columns.source")}</th>
                  <th style={cell}>{t("library.columns.format")}</th>
                  <th style={cell}>{t("library.columns.status")}</th>
                </tr>
              </thead>
              <tbody>
                {documents.data?.documents.map((document) => (
                  <tr key={document.id}>
                    <td style={cell}>
                      <DocumentTitle document={document} />
                    </td>
                    <td style={{ ...cell, fontFamily: "var(--mono)", fontSize: 12 }}>
                      {document.year ?? "—"}
                    </td>
                    <td style={cell}>
                      {document.wilayah ? (
                        <span className="faint">{document.wilayah}</span>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                    <td style={cell}>
                      <Pill>{document.source_type}</Pill>
                    </td>
                    <td style={{ ...cell, fontFamily: "var(--mono)", fontSize: 12 }}>
                      {document.format ?? "—"}
                    </td>
                    <td style={cell}>
                      <Pill
                        tone={
                          document.status === "indexed"
                            ? "green"
                            : document.status === "failed"
                              ? "red"
                              : "amber"
                        }
                      >
                        {t(`library.status.${document.status}`)}
                      </Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {documents.data && documents.data.documents.length < documents.data.total ? (
          <div style={{ marginTop: "var(--s4)" }}>
            {/* limit is capped at 200 server-side, so paging up is bounded on purpose. */}
            <Button onClick={() => setLimit((value) => Math.min(value + PAGE, 200))}>
              {t("library.more")}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * One title, made readable WITHOUT being rewritten.
 *
 * Measured on the real corpus: 43% of titles are ALL-CAPS (JDIH changed house style
 * mid-2022) and 67% run past 120 characters. Rendering them raw gives a wall of shouting
 * that wraps over three lines each, which is why the picker was unusable at 3,331 rows.
 *
 * The stored string is never touched — it is the document's legal identity, it appears in
 * every citation, and a reader checking us against jdih.acehprov.go.id needs it to match.
 * So: a short label from the legal grammar, the subject beneath it, and the full official
 * title on hover. A row that follows no legal grammar simply shows its own text.
 */
function DocumentTitle({ document }: { document: CorpusDocument }) {
  const { label, subject, full } = parseLegalTitle(document.title);
  return (
    <span title={full}>
      {label ? (
        <>
          <span className="mono" style={{ fontSize: 12 }}>
            {label}
          </span>
          <br />
        </>
      ) : null}
      <span>{truncateAtWord(subject, 110)}</span>
      {document.source_url ? (
        <>
          {" "}
          <a
            href={document.source_url}
            target="_blank"
            rel="noreferrer noopener"
            className="faint"
            style={{ fontSize: 12 }}
          >
            ↗
          </a>
        </>
      ) : null}
    </span>
  );
}

const cell: React.CSSProperties = {
  textAlign: "left",
  padding: "9px 12px",
  borderBottom: "1px solid var(--line-soft)",
  fontSize: 13.5,
};
